import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useRef, useState } from 'react';
import { ArrowLeft, Plus, Edit, Trash2, Play, Calendar, Search, ListPlus, ChevronDown } from 'lucide-react';
import {
  useTVShow,
  useEpisodesByTVShow,
  useDeleteEpisode,
  useDeleteEpisodes,
  useToggleEpisodeStatus,
  useCreateEpisode,
  useUpdateEpisode,
  useLinkTVShowToTmdb,
} from '@/hooks/useTVShows';
import { Episode, CreateEpisodeRequest } from '@/types/model';
import Button from '@/components/ui/Button';
import DataTable from '@/components/ui/DataTable';
import Modal from '@/components/ui/Modal';
import Toggle from '@/components/ui/Toggle';
import EpisodeForm from '@/components/forms/EpisodeForm';
import BulkEpisodeImport from '@/components/forms/BulkEpisodeImport';
import VodPlayer from '@/components/media/VodPlayer';
import { useClickOutside, useConfirmDialog, useTranslation } from '@/hooks';
import toast from 'react-hot-toast';
import { TableColumn } from '@/types';
import { PlaybackLanguageParams, tvShowService } from '@/services/tvShowService';

interface EpisodeAddButtonGroupProps {
  addLabel: string;
  bulkLabel: string;
  generatedBulkLabel: string;
  deleteLabel: string;
  compact?: boolean;
  onAdd: () => void;
  onBulkImport: () => void;
  onBulkGenerate: () => void;
  onBulkDelete: () => void;
}

const EpisodeAddButtonGroup = ({
  addLabel,
  bulkLabel,
  generatedBulkLabel,
  deleteLabel,
  compact = false,
  onAdd,
  onBulkImport,
  onBulkGenerate,
  onBulkDelete,
}: EpisodeAddButtonGroupProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const groupRef = useRef<HTMLDivElement>(null);
  useClickOutside(groupRef, () => setIsOpen(false));

  return (
    <div
      ref={groupRef}
      className="relative inline-flex shrink-0 whitespace-nowrap"
      onKeyDown={(event) => {
        if (event.key === 'Escape') setIsOpen(false);
      }}
    >
      <Button
        onClick={onAdd}
        title={addLabel}
        aria-label={addLabel}
        size="sm"
        className="rounded-r-none"
      >
        <Plus className={compact ? 'h-4 w-4' : 'mr-2 h-4 w-4'} />
        {!compact && addLabel}
      </Button>
      <Button
        onClick={() => setIsOpen((open) => !open)}
        title={bulkLabel}
        aria-label={bulkLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        size="sm"
        className="rounded-l-none border-l border-white/30 px-2"
      >
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>
      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 min-w-56 border border-gray-200 bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setIsOpen(false);
              onBulkImport();
            }}
            className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
          >
            <ListPlus className="h-4 w-4 text-gray-500" />
            {bulkLabel}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setIsOpen(false);
              onBulkGenerate();
            }}
            className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
          >
            <ListPlus className="h-4 w-4 text-gray-500" />
            {generatedBulkLabel}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setIsOpen(false);
              onBulkDelete();
            }}
            className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 focus:bg-red-50 focus:outline-none"
          >
            <Trash2 className="h-4 w-4" />
            {deleteLabel}
          </button>
        </div>
      )}
    </div>
  );
};

const TVShowEpisodes = () => {
  const navigate = useNavigate();
  const { id: tvShowId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { confirm, dialog } = useConfirmDialog();
  const [selectedSeason, setSelectedSeason] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingEpisode, setIsAddingEpisode] = useState(false);
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [selectedEpisodeIds, setSelectedEpisodeIds] = useState<string[]>([]);
  const [editingEpisode, setEditingEpisode] = useState<Episode | null>(null);
  const [playingEpisode, setPlayingEpisode] = useState<Episode | null>(null);

  const { data: tvShow, isLoading: tvShowLoading, isError: tvShowError } = useTVShow(tvShowId!);
  const {
    data: episodesResponse,
    isLoading: episodesLoading,
    refetch: refetchEpisodes
  } = useEpisodesByTVShow(tvShowId!, { _size: 1000 });

  const deleteEpisodeMutation = useDeleteEpisode();
  const deleteEpisodesMutation = useDeleteEpisodes();
  const toggleStatusMutation = useToggleEpisodeStatus();
  const createEpisodeMutation = useCreateEpisode();
  const updateEpisodeMutation = useUpdateEpisode();
  const linkTVShowToTmdbMutation = useLinkTVShowToTmdb();

  const loadPlayingEpisodeUrl = useCallback((params: PlaybackLanguageParams) => {
    if (!playingEpisode) {
      return Promise.reject(new Error('No episode selected'));
    }
    return tvShowService.getEpisodePlaybackUrl(playingEpisode.id, params);
  }, [playingEpisode]);

  const episodes = episodesResponse?.content || [];
  const totalSeasons = episodes.length > 0
    ? Math.max(...episodes.map((episode: Episode) => episode.seasonNumber))
    : 0;

  const filteredEpisodes = episodes.filter((episode: Episode) => {
    const matchesSeason = selectedSeason === 0 || episode.seasonNumber === selectedSeason;
    const matchesSearch =
      episode.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      episode.episodeNumber.toString().includes(searchTerm);
    return matchesSeason && matchesSearch;
  });

  const availableSeasons = Array.from(new Set(episodes.map((ep: Episode) => ep.seasonNumber))).sort((a: number, b: number) => a - b);

  const handleDeleteEpisode = async (episodeId: string) => {
    try {
      const confirmed = await confirm({
        title: t.common.confirmationTitle,
        message: '¿Está seguro de que desea eliminar este episodio?',
        confirmLabel: t.buttons.delete,
        cancelLabel: t.buttons.cancel,
        confirmVariant: 'danger',
        onConfirm: async () => {
          await deleteEpisodeMutation.mutateAsync({ episodeId, tvShowId: tvShowId! });
          await refetchEpisodes();
        },
      });

      if (!confirmed) {
        return;
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
      return;
    }

    toast.success('Episodio eliminado exitosamente');
  };

  const handleToggleStatus = async (episodeId: string, active: boolean) => {
    try {
      await toggleStatusMutation.mutateAsync({ episodeId, active, tvShowId: tvShowId! });
      refetchEpisodes();
    } catch (error) {
      console.error('Toggle status error:', error);
    }
  };

  const openBulkDelete = () => {
    setSelectedEpisodeIds([]);
    setIsBulkDeleting(true);
  };

  const handleBulkDelete = async () => {
    if (selectedEpisodeIds.length === 0) {
      toast.error(t.bulkEpisodes.noneSelected);
      return;
    }

    setIsBulkDeleting(false);
    try {
      const deletedCount = selectedEpisodeIds.length;
      const confirmed = await confirm({
        title: t.common.confirmationTitle,
        message: t.bulkEpisodes.deleteConfirmation.replace('{count}', String(deletedCount)),
        confirmLabel: t.bulkEpisodes.deleteSelected,
        cancelLabel: t.buttons.cancel,
        confirmVariant: 'danger',
        onConfirm: async () => {
          await deleteEpisodesMutation.mutateAsync({
            tvShowId: tvShowId!,
            episodeIds: selectedEpisodeIds,
          });
          await refetchEpisodes();
        },
      });

      if (confirmed) {
        setSelectedEpisodeIds([]);
        toast.success(t.bulkEpisodes.deleted.replace('{count}', String(deletedCount)));
      } else {
        setIsBulkDeleting(true);
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
      setIsBulkDeleting(true);
    }
  };

  const handleCreateEpisode = async (data: CreateEpisodeRequest) => {
    try {
      if (editingEpisode) {
        await updateEpisodeMutation.mutateAsync({ id: editingEpisode.id, data });
        setEditingEpisode(null);
      } else {
        await createEpisodeMutation.mutateAsync(data);
        setIsAddingEpisode(false);
      }
      refetchEpisodes();
    } catch (error) {
      console.error('Create error:', error);
      throw error;
    }
  };

  const handleCancel = () => {
    navigate('/content/tv-shows');
  };

  const episodeColumns: TableColumn<Episode>[] = [
    {
      key: 'episode',
      header: 'Episodio',
      accessor: (episode) => (
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            {episode.coverImageUrl ? (
              <img
                src={episode.coverImageUrl}
                alt={episode.title}
                className="h-12 w-16 rounded object-cover"
              />
            ) : (
              <div className="flex h-12 w-16 items-center justify-center rounded bg-gray-200">
                <Play className="h-4 w-4 text-gray-400"/>
              </div>
            )}
          </div>
          <div className="min-w-0">
            <div className="font-medium text-gray-900">
              S{episode.seasonNumber}E{episode.episodeNumber}: {episode.title}
            </div>
            <div className="mt-1 text-sm text-gray-600">
              {episode.airDate && (
                <span>{new Date(episode.airDate).toLocaleDateString()}</span>
              )}
              <span>{episode.airDate ? ' • ' : ''}
                {(episode.rating && episode.rating > 0) ? `${episode.rating}/10` : 'N/A'}
                </span>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'durationMinutes',
      header: 'Duración',
      accessor: (episode) => (
        <span className="text-sm text-gray-700">
          {episode.durationMinutes ? `${episode.durationMinutes} min` : '-'}
        </span>
      ),
      align: 'center',
      className: 'w-28',
    },
    {
      key: 'views',
      header: 'Visitas',
      accessor: (episode) => (
        <span className="text-sm text-gray-700">
          {episode.views?.toLocaleString() || 0}
        </span>
      ),
      align: 'right',
      className: 'w-28',
    },
    {
      key: 'active',
      header: 'Activo',
      accessor: (episode) => (
        <div className="flex justify-center">
          <Toggle
            checked={episode.active}
            onChange={(checked) => handleToggleStatus(episode.id, checked)}
            disabled={toggleStatusMutation.isPending}
            size="sm"
          />
        </div>
      ),
      align: 'center',
      className: 'w-24',
    },
    {
      key: 'actions',
      header: 'Acciones',
      accessor: (episode) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPlayingEpisode(episode)}
            disabled={!episode.links?.some((link) => link.active && Boolean(link.url?.trim()))}
            title={episode.links?.some((link) => link.active && Boolean(link.url?.trim()))
              ? t.vodPlayer.playEpisode
              : t.vodPlayer.noMedia}
            aria-label={episode.links?.some((link) => link.active && Boolean(link.url?.trim()))
              ? `${t.vodPlayer.playEpisode}: ${episode.title}`
              : t.vodPlayer.noMedia}
            className="text-indigo-600 hover:border-indigo-300 hover:text-indigo-800"
          >
            <Play className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditingEpisode(episode)}
          >
            <Edit className="w-4 h-4"/>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDeleteEpisode(episode.id)}
            className="text-red-600 hover:text-red-800 hover:border-red-300"
          >
            <Trash2 className="w-4 h-4"/>
          </Button>
        </div>
      ),
      align: 'center',
      className: 'w-44',
    },
  ];

  if (tvShowLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-600">Cargando serie...</div>
          </div>
        </div>
      </div>
    );
  }

  if (tvShowError || !tvShow) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="text-center py-12">
              <div className="text-red-600 mb-2">Error al cargar la serie</div>
              <Button onClick={handleCancel}>
                Volver a Series
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isBulkImporting) {
    return (
      <BulkEpisodeImport
        tvShow={tvShow}
        existingEpisodes={episodes}
        onLinkTVShowToTmdb={async (tmdbId) => {
          await linkTVShowToTmdbMutation.mutateAsync({ id: tvShow.id, tmdbId });
          toast.success(t.tmdbSearch.linkSuccess);
        }}
        onCancel={() => setIsBulkImporting(false)}
        onSuccess={async () => {
          setIsBulkImporting(false);
          await refetchEpisodes();
        }}
      />
    );
  }

  if (isBulkGenerating) {
    return (
      <BulkEpisodeImport
        source="generated"
        tvShow={tvShow}
        existingEpisodes={episodes}
        onCancel={() => setIsBulkGenerating(false)}
        onSuccess={async () => {
          setIsBulkGenerating(false);
          await refetchEpisodes();
        }}
      />
    );
  }

  if (isAddingEpisode || editingEpisode) {
    return (
      <div className="min-h-screen bg-gray-50 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="link"
            onClick={() => {
              setIsAddingEpisode(false);
              setEditingEpisode(null);
            }}
          >
            <ArrowLeft className="w-4 h-4 mr-2"/>
            Volver a Episodios
          </Button>

          <EpisodeForm
            tvShow={tvShow}
            existingEpisodes={episodes}
            onLinkTVShowToTmdb={async (tmdbId) => {
              await linkTVShowToTmdbMutation.mutateAsync({ id: tvShow.id, tmdbId });
              toast.success(t.tmdbSearch.linkSuccess);
            }}
            initialData={editingEpisode ? {
              tvShowId: editingEpisode.tvShowId,
              seasonNumber: editingEpisode.seasonNumber,
              episodeNumber: editingEpisode.episodeNumber,
              title: editingEpisode.title,
              spanishTitle: editingEpisode.spanishTitle,
              originalTitle: editingEpisode.originalTitle,
              airDate: editingEpisode.airDate,
              durationMinutes: editingEpisode.durationMinutes,
              rating: editingEpisode.rating,
              spanishSynopsis: editingEpisode.spanishSynopsis,
              englishSynopsis: editingEpisode.englishSynopsis,
              coverImageUrl: editingEpisode.coverImageUrl,
              active: editingEpisode.active,
              links: editingEpisode.links,
              id: editingEpisode.id,
              imdbId: editingEpisode.imdbId,
              tmdbId: editingEpisode.tmdbId,
            } : undefined}
            onSuccess={handleCreateEpisode}
            onCancel={() => {
              setIsAddingEpisode(false);
              setEditingEpisode(null);
            }}
            isEdit={!!editingEpisode}
            isSubmitting={createEpisodeMutation.isPending || updateEpisodeMutation.isPending || linkTVShowToTmdbMutation.isPending}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="link"
            onClick={handleCancel}
            className="mb-0"
          >
            <ArrowLeft className="w-4 h-4 mr-2"/>
            Volver a Series
          </Button>
          <Button
            variant="link"
            onClick={() => navigate(`/content/tv-shows/${tvShowId}/edit`)}
            className="mb-0"
          >
            Editar serie
            <Edit className="w-4 h-4 ml-2"/>
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-[320px_minmax(0,1fr)] md:items-start">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start gap-6 md:flex-col">
              {tvShow.coverImageUrl && (
                <img
                  src={tvShow.coverImageUrl}
                  alt={tvShow.title}
                  className="w-24 h-36 rounded-lg object-cover md:w-full md:max-w-[220px]"
                />
              )}
              <div className="flex-1">
                <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
                  {tvShow.title}
                </h1>
                <p className="mt-1 text-gray-600">
                  Gestión de episodios - {tvShow.year}
                </p>
                <div className="mt-3 flex items-center gap-4 text-sm text-gray-500 md:flex-col md:items-start md:gap-2">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4"/>
                    {totalSeasons} temporadas
                  </span>
                  <span className="flex items-center gap-1">
                    <Play className="w-4 h-4"/>
                    {tvShow.totalEpisodes || 0} episodios
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm">
            {episodesLoading ? (
              <div className="p-8 text-center text-gray-600">
                Cargando episodios...
              </div>
            ) : (
              <DataTable
                data={filteredEpisodes}
                columns={episodeColumns}
                paginate={false}
                tableStyle="compact"
                headerRenderer={() => (
                  <div className="border-b border-gray-200 bg-white">
                    <div className="px-4 py-2 md:hidden">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-medium text-gray-900">Episodios</h2>
                          <span
                            className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                            {filteredEpisodes.length}
                          </span>
                        </div>
                        <EpisodeAddButtonGroup
                          compact
                          addLabel={t.bulkEpisodes.addEpisode}
                          bulkLabel={t.bulkEpisodes.title}
                          generatedBulkLabel={t.bulkEpisodes.generatedTitle}
                          deleteLabel={t.bulkEpisodes.deleteEpisodes}
                          onAdd={() => setIsAddingEpisode(true)}
                          onBulkImport={() => setIsBulkImporting(true)}
                          onBulkGenerate={() => setIsBulkGenerating(true)}
                          onBulkDelete={openBulkDelete}
                        />
                      </div>
                      <div className="mt-3 grid grid-cols-1 gap-3">
                        <div className="min-w-0">
                          <select
                            value={selectedSeason}
                            onChange={(e) => setSelectedSeason(Number(e.target.value))}
                            className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value={0}>Todas las temporadas</option>
                            {availableSeasons.map((season: number) => (
                              <option key={season} value={season}>
                                Temporada {season}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="relative min-w-0">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4"/>
                          <input
                            type="text"
                            placeholder="Buscar por título o número..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="h-9 w-full rounded-md border border-gray-300 pl-10 pr-4 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="hidden flex-wrap items-center gap-3 px-4 py-2 md:flex xl:flex-nowrap">
                      <h2 className="text-lg font-medium text-gray-900">Episodios</h2>
                      <span
                        className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                        {filteredEpisodes.length}
                      </span>
                      <div className="min-w-[220px]">
                        <select
                          value={selectedSeason}
                          onChange={(e) => setSelectedSeason(Number(e.target.value))}
                          className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value={0}>Todas las temporadas</option>
                          {availableSeasons.map((season: number) => (
                            <option key={season} value={season}>
                              Temporada {season}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="relative min-w-[220px] flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4"/>
                        <input
                          type="text"
                          placeholder="Buscar por título o número..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="h-9 w-full rounded-md border border-gray-300 pl-10 pr-4 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <EpisodeAddButtonGroup
                        addLabel={t.bulkEpisodes.addEpisode}
                        bulkLabel={t.bulkEpisodes.title}
                        generatedBulkLabel={t.bulkEpisodes.generatedTitle}
                        deleteLabel={t.bulkEpisodes.deleteEpisodes}
                        onAdd={() => setIsAddingEpisode(true)}
                        onBulkImport={() => setIsBulkImporting(true)}
                        onBulkGenerate={() => setIsBulkGenerating(true)}
                        onBulkDelete={openBulkDelete}
                      />
                    </div>
                  </div>
                )}
              />
            )}
          </div>
        </div>
        <Modal
          isOpen={Boolean(playingEpisode)}
          onClose={() => setPlayingEpisode(null)}
          title={playingEpisode
            ? `${t.vodPlayer.title}: S${playingEpisode.seasonNumber}E${playingEpisode.episodeNumber} — ${playingEpisode.title}`
            : t.vodPlayer.title}
          size="2xl"
        >
          {playingEpisode && (
            <VodPlayer
              links={playingEpisode.links}
              poster={playingEpisode.coverImageUrl ?? tvShow.coverImageUrl}
              title={playingEpisode.title}
              loadPlaybackUrl={loadPlayingEpisodeUrl}
            />
          )}
        </Modal>
        <Modal
          isOpen={isBulkDeleting}
          onClose={() => setIsBulkDeleting(false)}
          title={t.bulkEpisodes.deleteTitle}
          size="lg"
          closeOnBackdrop={!deleteEpisodesMutation.isPending}
          closeOnEsc={!deleteEpisodesMutation.isPending}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">{t.bulkEpisodes.deleteDescription}</p>
            <label className="flex cursor-pointer items-center gap-3 border-b border-gray-200 pb-3 font-medium text-gray-900">
              <input
                type="checkbox"
                checked={episodes.length > 0 && selectedEpisodeIds.length === episodes.length}
                onChange={(event) => setSelectedEpisodeIds(
                  event.target.checked ? episodes.map((episode: Episode) => episode.id) : [],
                )}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              {t.bulkEpisodes.selectAll}
            </label>
            <div className="max-h-96 space-y-1 overflow-y-auto pr-1">
              {episodes.map((episode: Episode) => (
                <label
                  key={episode.id}
                  className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedEpisodeIds.includes(episode.id)}
                    onChange={(event) => setSelectedEpisodeIds((current) => (
                      event.target.checked
                        ? [...current, episode.id]
                        : current.filter((id) => id !== episode.id)
                    ))}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    S{episode.seasonNumber}E{episode.episodeNumber}: {episode.title}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between border-t border-gray-200 pt-4">
              <span className="text-sm text-gray-600">
                {t.bulkEpisodes.selected
                  .replace('{selected}', String(selectedEpisodeIds.length))
                  .replace('{total}', String(episodes.length))}
              </span>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setIsBulkDeleting(false)}>
                  {t.buttons.cancel}
                </Button>
                <Button
                  variant="danger"
                  onClick={handleBulkDelete}
                  disabled={selectedEpisodeIds.length === 0}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t.bulkEpisodes.deleteSelected}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
        {dialog}
      </div>
    </div>
  );
};

export default TVShowEpisodes;
